import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Handle scroll effect for gradual background opacity
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      setIsUserMenuOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isLandingPage) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const handleSectionClick = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    if (isLandingPage) {
      const element = document.getElementById(sectionId.replace('#', ''));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate(`/#${sectionId.replace('#', '')}`);
      // Wait for navigation then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId.replace('#', ''));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  // Determine if the current page is the landing page
  const isLandingPage = location.pathname === '/';
  
  // Determine if the current page is a design-related page
  const isDesignPage = location.pathname === '/design' || location.pathname.startsWith('/design/ai') || location.pathname.startsWith('/design/manual');

  // Determine if the current page is the MyDesigns page
  const isMyDesignsPage = location.pathname === '/my-designs';

  // Hide navbar on login and signup pages
  const shouldHideNavbar = location.pathname === '/login' || location.pathname === '/signup';

  // Calculate background opacity based on scroll position for landing page
  const bgOpacity = isLandingPage ? Math.min(scrollY / 200, 0.95) : 0.95; // Always solid on other pages
  const bgColor = `rgba(255, 255, 255, ${bgOpacity})`;
  const shadowOpacity = isLandingPage ? Math.min(scrollY / 100, 0.3) : 0.3; // Always shadow on other pages

  // Determine if the navbar should be transparent (only on landing page, at top)
  const isTransparentNavbar = isLandingPage && scrollY < 100;

  // Dynamic text and button colors based on transparency
  const textColor = isTransparentNavbar ? 'text-white' : 'text-slate-800';
  const subTextColor = isTransparentNavbar ? 'text-white/80' : 'text-slate-600';
  const linkColor = isTransparentNavbar ? 'text-white/90 hover:text-white' : 'text-slate-700 hover:text-slate-900';
  const buttonBorderColor = isTransparentNavbar ? 'border-white' : 'border-[#a57a6a]';
  const buttonTextColor = isTransparentNavbar ? 'text-white' : 'text-[#a57a6a]';
  const buttonHoverBg = isTransparentNavbar ? 'hover:bg-white/10' : 'hover:bg-[#a57a6a]/10';
  const buttonHoverBorder = isTransparentNavbar ? 'hover:border-white/80' : 'hover:border-[#a57a6a]/80';
  const mobileMenuTextColor = 'text-slate-700 hover:text-slate-900';
  const mobileMenuBgColor = 'bg-white/95 backdrop-blur-lg';

  // Don't render navbar on login/signup pages
  if (shouldHideNavbar) {
    return null;
  }

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-40 transition-all duration-300 backdrop-blur-sm"
      style={{ 
        backgroundColor: isTransparentNavbar ? 'transparent' : bgColor,
        boxShadow: isTransparentNavbar ? 'none' : (shadowOpacity > 0 ? `0 4px 20px rgba(0, 0, 0, ${shadowOpacity})` : 'none')
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            onClick={handleLogoClick}
            className="flex items-center space-x-3 rtl:space-x-reverse group cursor-pointer"
          >
            <div className="w-14 h-14 lg:w-14 lg:h-14 bg-gradient-to-br from-[#a57a6a] to-[#a57a6a] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
              <img
                src="/Logo_icon--oo--white--t.png"
                alt=" Abaya DIY Logo"
                className="w-10 h-10 lg:w-10 lg:h-10 object-contain"
                draggable={false}
              />
            </div>
            <div className="text-center">
              <div 
                className={`text-2xl lg:text-2xl font-bold group-hover:text-[#a57a6a] transition-colors duration-300 ${
                  textColor
                }`}
              >
                 Abaya DIY
              </div>
              <div 
                className={`text-xs lg:text-xs group-hover:text-[#e1b09f] transition-colors duration-300 ${
                  subTextColor
                }`}
              >
                عَبايتكْ منْ وحيِ خيالكَ
              </div>
            </div>
            
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {/* Landing page section navigation - only for non-authenticated users and on landing page */}
            {!user && isLandingPage && (
              <div className="flex items-center space-x-6 rtl:space-x-reverse">
                <a 
                  href="#how-it-works" 
                  className={`font-medium transition-all duration-300 hover:scale-105 relative group ${
                    linkColor
                  }`}
                >
                  كيف تعمل المنصة
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#a57a6a] transition-all duration-300 group-hover:w-full"></span>
                </a>
                
                <a 
                  href="#why-choose-us" 
                  onClick={(e) => handleSectionClick(e, '#why-choose-us')}
                  className={`font-medium transition-all duration-300 hover:scale-105 relative group ${
                    linkColor
                  }`}
                >
                  المميزات
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#a57a6a] transition-all duration-300 group-hover:w-full"></span>
                </a>
                <a 
                  href="#cta-banner" 
                  className={`font-medium transition-all duration-300 hover:scale-105 relative group ${
                    linkColor
                  }`}
                >
                  ما هي Abaya DIY؟
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#a57a6a] transition-all duration-300 group-hover:w-full"></span>
                </a>
                <a 
                  href="#faq" 
                  className={`font-medium transition-all duration-300 hover:scale-105 relative group ${
                    linkColor
                  }`}
                >
                  الأسئلة الشائعة
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#a57a6a] transition-all duration-300 group-hover:w-full"></span>
                </a>
                <a 
                  href="#contact" 
                  className={`font-medium transition-all duration-300 hover:scale-105 relative group ${
                    linkColor
                  }`}
                >
                  تواصلي معنا
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#a57a6a] transition-all duration-300 group-hover:w-full"></span>
                </a>
              </div>
            )}
<div className="my-4" />
            {/* Auth Buttons or User Menu */}
            {user ? (
              <div className="flex items-center space-x-4 rtl:space-x-reverse">
                {/* Only show "Start Designing" button when NOT on design pages */}
                {!isLandingPage && (
                    <Link
                      to="/"
                      className={`px-6 py-2.5 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 bg-transparent backdrop-blur-sm border ${buttonBorderColor} ${buttonTextColor} ${buttonHoverBg} ${buttonHoverBorder}`}
                    >
                      الصفحة الرئيسية
                    </Link>
                  )}
                {!isDesignPage && (
                  <Link
                    to="/design/ai"
                    className={`px-6 py-2.5 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 bg-transparent backdrop-blur-sm border ${buttonBorderColor} ${buttonTextColor} ${buttonHoverBg} ${buttonHoverBorder}`}
                  >
                    ابدئي التصميم
                  </Link>
                )}
                {/* Only show "My Designs" button when NOT on MyDesignsPage */}
                {!isMyDesignsPage && (

                  
                  <Link
                    to="/my-designs"
                    className={`px-6 py-2.5 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 bg-transparent backdrop-blur-sm border ${buttonBorderColor} ${buttonTextColor} ${buttonHoverBg} ${buttonHoverBorder}`}
                  >
                    تصاميمي
                  </Link>
                )}
                
                {/* User Menu */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center space-x-2 rtl:space-x-reverse px-4 py-2 bg-gradient-to-r from-[#a57a6a] to-[#a57a6a] hover:from-[#8a6a5a] hover:to-[#8a6a5a] text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-[#a57a6a]/25"
                  >
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold">
                        {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="hidden sm:block">{user.name || user.email}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* User Dropdown Menu */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-xl.shadow-xl text-right">
                      <div className="py-2">
                        <div className="px-4 py-2 border-b border-slate-200">
                          <p className="text-sm font-medium text-slate-800">{user.name}</p>
                          <p className="text-xs text-slate-600">{user.email}</p>
                        </div>
                        <button
                          onClick={handleLogout}
                          className="w-full text-right px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors.duration-200"
                        >
                          تسجيل الخروج
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-4 rtl:space-x-reverse">
                <Link
                  to="/login"
                  className={`px-6 py-2.5 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                    isActive('/login')
                      ? 'bg-gradient-to-r from-[#a57a6a] to-[#a57a6a] text-white shadow-lg shadow-[#a57a6a]/25'
                      : `bg-transparent backdrop-blur-sm border ${buttonBorderColor} ${buttonTextColor} ${buttonHoverBg} ${buttonHoverBorder}`
                  }`}
                >
                  تسجيل الدخول
                </Link>
                <Link
                  to="/signup"
                  className={`"flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#a57a6a] to-[#a57a6a] hover:from-[#8a6a5a] hover:to-[#8a6a5a] text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-[#a57a6a]/25" ${
                    isActive('/signup')
                      ? 'bg-gradient-to-r from-[#a57a6a] to-[#a57a6a] text-white shadow-lg shadow-[#a57a6a]/25'
                      : 'bg-[#a57a6a]/20 hover:bg-[#a57a6a]/30 text-white border border-[#a57a6a]/40 backdrop-blur-sm'
                  }`}
                >
              إنشاء حساب   
              </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 rounded-lg transition-colors duration-300 hover:bg-white/10 ${
                isTransparentNavbar ? 'text-white hover:text-white/80' : 'text-slate-700 hover:text-slate-900'
              }`}
              aria-label={isMobileMenuOpen ? 'إغلاق القائمة' : 'فتح القائمة'}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className={`lg:hidden transition-all duration-300 ${mobileMenuBgColor} border-t border-white/20`}>
            <div className="px-2 pt-2 pb-3 space-y-1">
              {/* Landing page section navigation - only for non-authenticated users and on landing page */}
              {!user && isLandingPage && (
                <>
                  <a 
                    href="#how-it-works" 
                    className={`block px-3 py-2 rounded-lg text-base font-medium ${mobileMenuTextColor} hover:bg-slate-50 transition-colors duration-300`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    كيف تعمل المنصة
                  </a>
                  <a 
                    href="#why-choose-us" 
                    className={`block px-3 py-2 rounded-lg text-base font-medium ${mobileMenuTextColor} hover:bg-slate-50 transition-colors duration-300`}
                    onClick={(e) => {
                      handleSectionClick(e, '#why-choose-us');
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    المميزات
                  </a>
                  <a 
                  href="#cta-banner" 
                  className={`block px-3 py-2 rounded-lg text-base font-medium ${mobileMenuTextColor} hover:bg-slate-50 transition-colors duration-300`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  >
                  ما هي Abaya DIY؟
                </a>
                  <a 
                    href="#faq" 
                    className={`block px-3 py-2 rounded-lg text-base font-medium ${mobileMenuTextColor} hover:bg-slate-50 transition-colors duration-300`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    الأسئلة الشائعة
                  </a>
                  <a 
                    href="#contact" 
                    className={`block px-3 py-2 rounded-lg text-base font-medium ${mobileMenuTextColor} hover:bg-slate-50 transition-colors duration-300`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    تواصلي معنا
                  </a>
                </>
              )}

              {/* Auth Buttons for Mobile */}
              {user ? (
                <div className="space-y-2 pt-4 border-t border-slate-200">
                  {/* Only show "Start Designing" button when NOT on design pages */}
                  {!isLandingPage && (
                    <Link
                      to="/"
                      className="block w-full text-center px-4 py-2 bg-gradient-to-r from-[#a57a6a] to-[#a57a6a] hover:from-[#8a6a5a] hover:to-[#8a6a5a] text-white font-semibold rounded-xl transition-all duration-300"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
الصفحة الرئيسية
      </Link>
                  )}
                  
                  {!isDesignPage && (
                    <Link
                      to="/design/ai"
                      className="block w-full text-center px-4 py-2 bg-gradient-to-r from-[#a57a6a] to-[#a57a6a] hover:from-[#8a6a5a] hover:to-[#8a6a5a] text-white font-semibold rounded-xl transition-all duration-300"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      ابدئي التصميم
                    </Link>
                  )}
                  
                  {/* Only show "My Designs" button when NOT on MyDesignsPage */}
                  {!isMyDesignsPage && (
                    <Link
                      to="/my-designs"
                      className="block w-full text-center px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all duration-300"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      تصاميمي
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full text-center px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-all duration-300"
                  >
                    تسجيل الخروج
                  </button>
                </div>
              ) : (
                
                <div className="space-y-2 pt-4 border-t border-slate-200">
                  <Link
                    to="/login"
                    className={`block w-full text-center px-4 py-2 rounded-xl font-semibold transition-all duration-300 ${
                      isActive('/login')
                        ? 'bg-gradient-to-r from-[#a57a6a] to-[#a57a6a] text-white'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    تسجيل الدخول
                  </Link>
                  <Link
                    to="/signup"
                    className={`block w-full text-center px-4 py-2 rounded-xl font-semibold transition-all duration-300 ${
                      isActive('/signup')
                        ? 'bg-gradient-to-r from-[#a57a6a] to-[#a57a6a] text-white'
                        : 'bg-slate-800 hover:bg-slate-700 text-white'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                   إنشاء حساب
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
